import { LoadingSpinner } from '@components/Icons/LoadingSpinner';
import { Modal } from '@components/Modal';
import { useSubscriptionDetails } from '@hooks/apiHooks/subscriptionHooks';
import { useInviteUsers } from '@hooks/apiHooks/userHooks';
import { showToast } from '@utils/toast';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { OidcUser } from '../../../../types/oidcUser';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
}

const { VITE_SITE_ORIGIN } = import.meta.env;

const InviteMemberModal: FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  organizationId,
}) => {
  const { t } = useTranslation();
  const { mutate: inviteUsers, isPending } = useInviteUsers();
  const { user, isAuthenticated } = useAuth();
  const { data: subscriptionData } = useSubscriptionDetails(
    { organization_id: organizationId },
    isAuthenticated,
  );

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const handleClose = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    onClose();
  };

  if (!isOpen) return null;

  const isFormValid =
    email.trim() !== '' && firstName.trim() !== '' && lastName.trim() !== '';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) {
      return;
    }

    const oidcUser = user as OidcUser;
    const userId = oidcUser?.profile?.sub || '';
    const subscriptionId =
      subscriptionData?.billingDetails?.subscriptionId || '';

    inviteUsers(
      {
        organizationId,
        subscriptionId,
        invitee: { email, firstName, lastName },
        inviter: { id: userId },
        url: VITE_SITE_ORIGIN + '/callbackSignIn',
        roles: ['member'],
      },
      {
        onSuccess: () => {
          showToast.success(t('account.invitationSent'));
          handleClose();
        },
        onError: (error: any) => {
          const errorMessage =
            error?.response?.data?.error || t('account.invitationFailed');
          showToast.error(errorMessage);
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('account.inviteTitle')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            {t('account.emailLabel')}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('account.emailPlaceholder')}
            required
            className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-white/40"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">
            {t('account.firstNameLabel')}
          </label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder={t('account.firstNameExample')}
            required
            className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-white/40"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">
            {t('account.lastNameLabel')}
          </label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder={t('account.lastNameExample')}
            required
            className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-white/40"
          />
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={isPending}
            className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg font-medium transition-colors disabled:opacity-50">
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={isPending || !isFormValid}
            className="flex items-center justify-center gap-2 flex-1 px-4 py-2 bg-white hover:bg-white/90 text-black rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {isPending && (
              <LoadingSpinner width={16} height={16} color="#000000" />
            )}
            {t('account.sendInvitation')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default InviteMemberModal;
